import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { handleLogout } from '../../../config/axios';
import decryptData from '../../../decrypt';

export interface Order {
  NUMERO_ORDINE: string;
  RAGIONE_SOCIALE: string;
  DATA_ORDINE: string | number;
  DATA_CONSEGNA_RICHIESTA: string | number;
  DEPOSITO: string;
  RITIRO_CLIENTE: string;
  TIPO_CONSEGNA: string;
  STATO_ORDINE: string;
  RIFERIMENTO_CLIENTE?: string;
  SOMMA_RAE?: number;
  SOMMA_IMPORTO?: number;
}

export interface Sum {
  SOMMA_RAE?: number;
  SOMMA_IMPORTO?: number;
}

export const savePdf = async (filters): Promise<void> => {
  try {
    // Preparazione del payload (le date vengono convertite in formato YYYYMMDD)
    const payload = {
      data_ordine: [
        filters.from.replace(/-/g, ""),
        filters.to.replace(/-/g, "")
      ],
      agenti: filters.agenti,
      evaso: filters.evaso,
      ritiro_cliente: filters.ritiro_cliente,
      tipologia_consegna: filters.tipologia_consegna,
      page: 1,
      pageSize: 10000, // Recuperiamo fino a 10.000 record
      campo_ordinamento: filters.campo_ordinamento || "data_ordine",
      tipo_ordinamento: filters.tipo_ordinamento || "DESC",
      codice_cliente: filters.codice_cliente
    };

    console.log("Payload per il PDF:", JSON.stringify(payload, null, 2));

    // Chiamata API
    const response = await fetch("/nodeapi/ordini", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`
      },
      body: JSON.stringify(payload)
    });


     if (response.status === 401 || response.status === 403 || response.status === 500) {
        handleLogout();
        return;
      }

    const result = await response.json();
    const decrypted = decryptData(
      result.encryptedData,
      result.encryptedKey,
      result.iv
    );
    const decryptedJson = JSON.parse(decrypted);

    if (decryptedJson.status !== "success") {
      console.error("Errore nel recupero dati per il PDF.");
      return;
    }

    const data: Order[] = decryptedJson.data;

    // Calcolo dei totali per i campi SOMMA_IMPORTO e SOMMA_RAE
    const sum: Sum = data.reduce((acc: Sum, order: Order) => {
      acc.SOMMA_IMPORTO = (acc.SOMMA_IMPORTO || 0) + (order.SOMMA_IMPORTO || 0);
      acc.SOMMA_RAE = (acc.SOMMA_RAE || 0) + (order.SOMMA_RAE || 0);
      return acc;
    }, {});

    generatePdf(data, sum);
  } catch (error) {
    console.error("Errore durante la generazione del PDF:", error);
  }
};

// Helper function to detect mobile devices
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
};

// Helper function to download PDF with mobile compatibility
const downloadPdfWithMobileSupport = (blob: Blob, fileName: string) => {
  const blobUrl = URL.createObjectURL(blob);
  
  if (isMobileDevice()) {
    // For mobile devices, use window.open which is more reliable
    const newWindow = window.open(blobUrl, '_blank');
    if (!newWindow) {
      // If popup blocked, fallback to creating a link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Dispatch a click event instead of calling click()
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      link.dispatchEvent(clickEvent);
      
      // Clean up after a delay
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } else {
      // Clean up blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    }
  } else {
    // Desktop approach
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 100);
  }
};

const generatePdf = (data: Order[], sum: Sum): void => {
  try {
    const doc = new jsPDF("landscape", "in", "a4");

    const marginLeft = 0.5;
    const marginTop = 0.5;

    // Aggiunta del logo
    const logoWidth = 0.5;
    const logoHeight = 0.5;
    doc.addImage("/assets/cedi.png", "PNG", marginLeft, marginTop, logoWidth, logoHeight);

    // Informazioni della sede legale
    doc.setFontSize(10);
    const legalX = marginLeft + logoWidth + 0.2;
    let currentY = marginTop;
    doc.text("Sede Legale", legalX, currentY + 0.2);
    doc.text("Via Luca da Penne, 3", legalX, currentY + 0.35);
    doc.text("80122 - NAPOLI", legalX, currentY + 0.5);
    doc.text("C.F: 04805240639 - P.I. 04805240639", legalX, currentY + 0.65);
    doc.text("REA Napoli n. 396782", legalX, currentY + 0.8);

    // Box dei totali
    const boxWidth = 3;
    const boxHeight = 1;
    const pageWidth = doc.internal.pageSize.getWidth();
    const boxX = pageWidth - marginLeft - boxWidth;
    const boxPadding = 0.15;

    // Box di sfondo con bordi arrotondati
    doc.setFillColor(248, 248, 248);
    doc.roundedRect(boxX, marginTop, boxWidth, boxHeight, 0.1, 0.1, "F");

    // Bordo sottile
    doc.setDrawColor(221, 221, 221);
    doc.setLineWidth(0.01);
    doc.roundedRect(boxX, marginTop, boxWidth, boxHeight, 0.1, 0.1, "S");

    // Formattazione dei valori
    const totaleProdotti = Number(sum.SOMMA_IMPORTO || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
    const totaleTrasporto = Number(sum.SOMMA_RAE || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
    const totaleOrdini = Number((sum.SOMMA_IMPORTO || 0) + (sum.SOMMA_RAE || 0)).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

    // Inserimento dei totali nel box
    const lineSpacing = 0.2;
    let textY = marginTop + boxPadding + 0.1;
    const valueX = boxX + boxWidth - boxPadding;
    const labelX = boxX + boxPadding;

    // Totale Prodotti
    doc.text("Totale Prodotti:", labelX, textY);
    doc.text(totaleProdotti, valueX, textY, { align: "right" });

    // Spesa Trasporto
    textY += lineSpacing;
    doc.text("Importo RAE:", labelX, textY);
    doc.text(totaleTrasporto, valueX, textY, { align: "right" });

    // Linea separatrice
    textY += lineSpacing;
    doc.setDrawColor(200, 200, 200);
    doc.line(labelX, textY, valueX, textY);

    // Totale Ordini (in grassetto)
    textY += lineSpacing;
    doc.setFont(undefined, "bold");
    doc.text("TOTALE ORDINI:", labelX, textY);
    doc.text(totaleOrdini, valueX, textY, { align: "right" });
    doc.setFont(undefined, "normal");

    // Titolo della lista ordini
    currentY += 1.2;
    const title = `LISTA ORDINI ${moment().format("DD/MM/YYYY")}`;
    doc.setFontSize(12);
    doc.text(title, marginLeft, currentY);

    // Preparazione dei dati della tabella
    const tableHead = [
      "N° ORDINE",
      "RAGIONE SOCIALE",
      "DATA ORDINE",
      "DATA CONSEGNA",
      "DEPOSITO",
      "RITIRO CLIENTE",
      "TIPO CONSEGNA",
      "STATO",
      "RIF. CLIENTE",
      "TOTALE PRODOTTI",
      "IMPORTO RAE",
      "TOTALE ORDINE"
    ];

    const tableRows = data.map((order) => {
      const dataOrdine = moment(order.DATA_ORDINE.toString(), "YYYYMMDD").format("DD/MM/YYYY");
      const dataConsegna = moment(order.DATA_CONSEGNA_RICHIESTA.toString(), "YYYYMMDD").format("DD/MM/YYYY");
      const totale = (order.SOMMA_IMPORTO || 0) + (order.SOMMA_RAE || 0);
      return [
        order.NUMERO_ORDINE,
        order.RAGIONE_SOCIALE,
        dataOrdine,
        dataConsegna,
        order.DEPOSITO,
        order.RITIRO_CLIENTE,
        order.TIPO_CONSEGNA,
        order.STATO_ORDINE === "S" ? "Evaso" : "Non evaso",
        order.RIFERIMENTO_CLIENTE || "",
        Number(order.SOMMA_IMPORTO || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" }),
        Number(order.SOMMA_RAE || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" }),
        Number(totale).toLocaleString("it-IT", { style: "currency", currency: "EUR" })
      ];
    });

    const tableStartY = currentY + 0.3;
    let pageNumber = 1;

    autoTable(doc, {
      startY: tableStartY,
      head: [tableHead],
      body: tableRows,
      styles: {
        fontSize: 8,
        cellPadding: 0.1,
        lineColor: [221, 221, 221],
        lineWidth: 0.01
      },
      headStyles: {
        fillColor: [248, 248, 248],
        textColor: [0, 0, 0],
        fontStyle: "bold"
      },
      margin: { left: marginLeft, right: marginLeft },
      didDrawPage: (dataArg) => {
        if (dataArg.pageNumber > pageNumber) {
          pageNumber = dataArg.pageNumber;
        }
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.getWidth();
        const pageHeight = pageSize.getHeight();
        doc.setFontSize(8);
        doc.text(`Pagina ${pageNumber}`, pageWidth - marginLeft, pageHeight - 0.3, { align: "right" });
      }
    });

    const filename = `ordini_${moment().format("DDMMYYYY")}.pdf`;
    
    // Generate PDF as blob instead of using save()
    const pdfBlob = doc.output('blob');
    
    // Use mobile-compatible download method
    downloadPdfWithMobileSupport(pdfBlob, filename);
  } catch (error) {
    console.error("Errore durante la generazione del PDF:", error);
  }
};
